import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Check, Copy, Link2, Lock, Trash2 } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { createReferralLink, finishPartnerOnboarding, getPartnerOnboarding } from '../../services/api';
import type { FrameworkField, FrameworkView, IntakeSection } from '../../types/aai';
import { OrbitDeck } from '../../components/orbit-deck/OrbitDeck';
import type { DeckBodyContext, DeckCardModel } from '../../components/orbit-deck/types';
// Parked with the old intake cards.
// import FieldStudio from '../super/FieldStudio';
import './PartnerWelcome.css';

/*
 * The welcome deck for a Solution Partner who arrived through an invite link.
 *
 * One framework, ours, for every partner: there is no choice to make and no
 * documents to read. The deck shows the six opening questions every client's
 * Owner answers first, fixed, then each of our four intakes in turn, where the
 * partner hides questions of ours and adds their own, with the same editor as
 * The intake on the Intake framework tab. Then their client link.
 *
 * Who answers what is ours too: the Owner the business and tool intakes, each
 * department head their department's, and everybody their own person intake.
 *
 * Finishing, or skipping, stamps the partner as onboarded and lands on the
 * Intake framework tab, where everything set here can be changed again.
 */

const REFERRAL_DAYS = 30;
const SMALL = { fontSize: 12, color: 'var(--aa-fg-2)' } as const;

/* The three groups of questions a client answers after setup, and who
   answers each. Setup itself (role, department, apps, permissions) is the
   same for every client. The Tool intake is parked: setup asks about apps. */
/* Parked: one intake for every role now, People and Tools fixed and the
   partner's own sections after them.
const INTAKES: { id: string; label: string; who: string; blurb: string }[] = [
  { id: 'business', label: 'Company · Owner', who: 'Answered by the Owner', blurb: 'Questions about the company itself, asked once after setup.' },
  // { id: 'tool', label: 'Tool intake', who: 'Answered by the Owner', blurb: 'Every question Orbit asks about one tool the company uses.' },
  { id: 'department', label: 'Department · heads', who: 'Answered by each department head', blurb: 'Questions about one department, of whoever runs it.' },
  { id: 'person', label: 'Everyone · each person', who: 'Answered by everybody: the Owner, each head and each employee', blurb: 'Questions every person answers about their own work, after setup.' },
];
*/

/* The admin may have typed an email where the name goes; greeting by it reads
   wrong, so the greeting drops the name rather than show an address. */
const firstNameOf = (name: string): string => {
  const first = (name || '').trim().split(/\s+/)[0] || '';
  if (!first || first.includes('@')) return '';
  return first;
};

const givesLine = (why: string | null, label: string): string => {
  const text = (why || '').trim();
  if (text) return text;
  return `Gives Orbit ${label.toLowerCase()}`;
};

const starterFields = (view: FrameworkView | null): FrameworkField[] => {
  if (!view) return [];
  const byKey = new Map(view.fields.map((f) => [f.key, f]));
  return view.starter.keys.map((k) => byKey.get(k)).filter((f): f is FrameworkField => Boolean(f));
};

/* ── the six, fixed ──────────────────────────────────────────────────────── */

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- parked with the opening card
const OpeningBody: React.FC<{ viewRef: React.MutableRefObject<FrameworkView | null> } & DeckBodyContext> = ({ viewRef, answer, busy }) => {
  const fields = starterFields(viewRef.current);
  return (
    <div className="odeck-free col">
      {fields.length === 0 && <p className="m-0 text-[13px] text-[#666971]">Reading the opening questions.</p>}
      <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
        {fields.map((f, i) => (
          <li key={f.key} className="flex items-start gap-3 rounded-[10px] border border-[#e7e7e4] bg-white px-3 py-2">
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#18191d] text-[10px] font-bold text-white">{i + 1}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-[#18191d]">{f.label}</span>
              <span className="block text-[12px] text-[#777982]">Gives Orbit · {givesLine(f.why, f.label)}</span>
            </span>
            <span className="shrink-0 rounded bg-[#f7f7f5] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#92949d]">always asked</span>
          </li>
        ))}
      </ol>
      <button type="button" className="go wide" disabled={busy} onClick={() => answer({ how: 'confirmed', value: 'seen' })}>Got it, next card</button>
      <small style={SMALL}>Set by Agents Anywhere. These six cannot be moved, dropped or reworded.</small>
    </div>
  );
};

/* ── one of our intakes, in The intake's own editor ──────────────────────── */

/* Parked with the old intake cards.
const IntakeBody: React.FC<{ intake: string; who: string } & DeckBodyContext> = ({ intake, who, answer, busy }) => (
  <div className="odeck-free col">
    <span className="text-[12.5px] font-semibold text-[#18191d]">{who}</span>
    <div className="partner-deck-editor">
      <FieldStudio mode="partner" embedded single openTab={intake} />
    </div>
    <button type="button" className="go wide" disabled={busy} onClick={() => answer({ how: 'confirmed', value: 'seen' })}>Done with this intake, next card</button>
  </div>
);
*/

/* ── a fixed section, set by Agents Anywhere ─────────────────────────────── */

const FixedBody: React.FC<{ section: IntakeSection } & DeckBodyContext> = ({ section, answer, busy }) => (
  <div className="odeck-free col">
    <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
      {section.questions.map((q, i) => (
        <li key={q.id} className="flex items-start gap-3 rounded-[10px] border border-[#e7e7e4] bg-white px-3 py-2">
          <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#18191d] text-[10px] font-bold text-white">{i + 1}</span>
          <span className="min-w-0 flex-1 text-[13px] font-semibold text-[#18191d]">{q.label}</span>
          <span className="inline-flex shrink-0 items-center gap-1 rounded bg-[#f7f7f5] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#92949d]"><Lock size={10} />fixed</span>
        </li>
      ))}
    </ol>
    <button type="button" className="go wide" disabled={busy} onClick={() => answer({ how: 'confirmed', value: 'seen' })}>Got it, next card</button>
    <small style={SMALL}>Set by Agents Anywhere for every client. The other intakes you can change on the Champion Framework page.</small>
  </div>
);

/* ── the partner's own sections ──────────────────────────────────────────── */

const AddSectionBody: React.FC<DeckBodyContext> = ({ answer, busy }) => {
  const [added, setAdded] = useState<IntakeSection[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<string[]>(['']);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    aaiApi.intakeSections().then((r) => setAdded(r.data.sections)).catch(() => undefined);
  }, []);

  const save = async () => {
    const asked = questions.map((q) => q.trim()).filter(Boolean);
    if (!title.trim()) { toast.error('Give the section a title.'); return; }
    if (asked.length === 0) { toast.error('Add at least one question.'); return; }
    setSaving(true);
    try {
      const r = await aaiApi.addIntakeSection({ title: title.trim(), description: description.trim(), questions: asked.map((label) => ({ label })) });
      setAdded(r.data.sections);
      setTitle('');
      setDescription('');
      setQuestions(['']);
      toast.success('Section added.');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'Could not add the section.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full rounded-[10px] border border-[#dedede] bg-white px-3 text-[13.5px] text-[#18191d] outline-none placeholder:text-[#92949d] focus:border-[#18191d]';
  let doneWord = 'Skip for now, next card';
  if (added.length > 0) doneWord = 'Done, next card';

  return (
    <div className="odeck-free col">
      {added.length > 0 && (
        <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
          {added.map((s) => (
            <li key={s.id} className="flex items-center gap-3 rounded-[10px] border border-[#e7e7e4] bg-white px-3 py-2">
              <Check size={14} className="shrink-0 text-[#1d7a45]" />
              <span className="min-w-0 flex-1 text-[13px] font-semibold text-[#18191d]">{s.title}</span>
              <span className="shrink-0 text-[12px] text-[#777982]">{s.questions.length} {s.questions.length === 1 ? 'question' : 'questions'}</span>
            </li>
          ))}
        </ol>
      )}
      <div className="space-y-3 rounded-[12px] border border-[#e7e7e4] bg-[#fafaf9] p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="welcome-section-title" className="odeck-label">Section title</label>
            <input id="welcome-section-title" className={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="For example, Goals" />
          </div>
          <div>
            <label htmlFor="welcome-section-description" className="odeck-label">Description <span className="font-normal text-[#92949d]">(optional)</span></label>
            <input id="welcome-section-description" className={input} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this section helps you understand" />
          </div>
        </div>
        <div>
          <span className="odeck-label">Questions</span>
          <div className="space-y-2">
            {questions.map((q, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-right text-[12px] text-[#92949d]">{i + 1}.</span>
                <input className={input} value={q} onChange={(e) => setQuestions((all) => all.map((x, n) => (n === i ? e.target.value : x)))} placeholder="Write a question" aria-label={`Question ${i + 1}`} />
                <button type="button" aria-label={`Remove question ${i + 1}`} disabled={questions.length === 1} onClick={() => setQuestions((all) => all.filter((_, n) => n !== i))} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#92949d] hover:bg-white hover:text-[#18191d] disabled:opacity-30">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#e7e7e4] pt-3">
          <button type="button" onClick={() => setQuestions((all) => [...all, ''])} className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#18191d] hover:underline">+ Add question</button>
          <button type="button" onClick={save} disabled={saving} className="rounded-[10px] bg-[#18191d] px-4 py-2.5 text-[13px] font-semibold text-white disabled:opacity-50">{saving ? 'Saving' : 'Save section'}</button>
        </div>
      </div>
      <button type="button" className="go wide" disabled={busy || saving} onClick={() => answer({ how: 'confirmed', value: 'seen' })}>{doneWord}</button>
    </div>
  );
};

/* ── the link ────────────────────────────────────────────────────────────── */

const LinkBody: React.FC<DeckBodyContext> = ({ answer, busy }) => {
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    createReferralLink(REFERRAL_DAYS)
      .then((r: { url: string }) => { if (live) setLink(r.url); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(`Client link copied. It works for ${REFERRAL_DAYS} days.`);
    } catch {
      toast.info('Select the link and copy it yourself.');
    }
  };
  let word = 'Copy';
  if (copied) word = 'Copied';
  return (
    <div className="odeck-free col">
      <div className="flex items-center gap-3 rounded-card bg-ink px-4 py-3 text-white">
        <Link2 size={16} strokeWidth={1.75} className="shrink-0 text-[#adadad]" />
        <span className={`min-w-0 flex-1 truncate text-[12.5px] ${link ? '' : 'text-[#adadad]'}`}>
          {link || (failed ? 'The link is on your Clients page whenever you need it.' : 'Preparing your link')}
        </span>
        {link && (
          <button type="button" onClick={copy} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-accent-500 px-3 text-[12px] font-semibold text-black whitespace-nowrap">
            {copied ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}{word}
          </button>
        )}
      </div>
      <button type="button" className="go wide" disabled={busy} onClick={() => answer({ how: 'confirmed', value: 'done' })}>Done, open my console</button>
      <small style={SMALL}>A client who opens it sets up their company under you, and goes through the intake you just set.</small>
    </div>
  );
};

/* ── the page ────────────────────────────────────────────────────────────── */

export const PartnerWelcome: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [ready, setReady] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [fixed, setFixed] = useState<IntakeSection[]>([]);

  const finish = useCallback(async (to: string) => {
    if (leaving) return;
    setLeaving(true);
    try { await finishPartnerOnboarding(); } catch { /* the console still opens; the deck is offered again next time */ }
    navigate(to, { replace: true });
  }, [leaving, navigate]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const me = await getPartnerOnboarding();
        if (!live) return;
        if (me.onboarded) { navigate('/partner/framework', { replace: true }); return; }
        setName(me.name);
      } catch { /* an unreadable state still gets the deck; finishing stamps it */ }
      try {
        const r = await aaiApi.intakeSections();
        if (live) setFixed(r.data.fixed.filter((s) => (s.intake || 'opening') === 'opening' && !s.removed));
      } catch { /* without them the deck still offers the partner's own sections */ }
      if (live) setReady(true);
    })();
    return () => { live = false; };
  }, [navigate]);

  const cards = useMemo<DeckCardModel[]>(() => [
    /* Parked: the opening questions are part of Company, asked after setup.
    {
      id: 'opening', tag: 'ask', tagLabel: 'Opening questions', title: 'The six questions every Owner answers first.',
      why: 'Asked of the Owner the moment a client starts, whether they register through your link or you add them. Always asked, in this order.',
      input: 'say', noSkip: true,
      footNote: 'The same six as Opening questions on the Intake framework tab.',
      body: (ctx) => <OpeningBody viewRef={viewRef} {...ctx} />,
    },
    */
    /* Parked: the old intake cards.
    ...INTAKES.map((intake, i): DeckCardModel => ({
      id: `intake:${intake.id}`, tag: 'ask', tagLabel: `The intake · ${i + 1} of ${INTAKES.length}`, title: intake.label,
      why: `${intake.blurb} Hide any question of ours, or add your own.`,
      input: 'say', noSkip: true,
      footNote: 'The same control as The intake on the Intake framework tab. Everything saves as you go.',
      body: (ctx) => <IntakeBody intake={intake.id} who={intake.who} {...ctx} />,
    })),
    */
    ...fixed.map((section, i): DeckCardModel => ({
      id: `fixed:${section.id}`, tag: 'ask', tagLabel: `Fixed section · ${i + 1} of ${fixed.length}`, title: section.title,
      why: section.description,
      input: 'say', noSkip: true,
      footNote: 'Asked first. The other intakes follow on the Champion Framework page.',
      body: (ctx) => <FixedBody section={section} {...ctx} />,
    })),
    {
      id: 'add_section', tag: 'ask', tagLabel: 'Your sections', title: 'Add your own sections.',
      why: 'Ask what your method needs to know. Your sections come after People and Tools, and everyone at your clients answers them after setup.',
      input: 'say', noSkip: true,
      footNote: 'Everything saves as you go. The Champion Framework page shows the same sections afterwards.',
      body: (ctx) => <AddSectionBody {...ctx} />,
    },
    {
      id: 'client_link', tag: 'confirm', tagLabel: 'Last card', title: 'Your client link.',
      why: 'Share it and a customer registers themselves under you. Their Owner walks the setup first, then answers your questions from the dashboard.',
      input: 'say', noSkip: true,
      footNote: `The link works for ${REFERRAL_DAYS} days. A fresh one is a click away on Clients.`,
      body: (ctx) => <LinkBody {...ctx} />,
    },
  ], [fixed]);

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-[#e7e7e4] bg-white/90 px-6 py-3.5 backdrop-blur">
        <img src="/logo-black.png" alt="AgentsAnywhere" className="h-7 w-auto object-contain" />
        <button type="button" onClick={() => finish('/partner/framework')} disabled={leaving} className="rounded-full border border-[#dedede] bg-white px-4 py-2 text-[13px] font-semibold text-[#18191d] disabled:opacity-50">Skip for now</button>
      </header>
      <main className="mx-auto max-w-[1200px] px-6 pb-20 pt-10">
        <div className="mb-6">
          <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#777982]">Solution Partner · welcome deck</p>
          <h1 className="m-0 text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-[#18191d] sm:text-[34px]">Welcome{firstNameOf(name) ? `, ${firstNameOf(name)}` : ''}.<br /><span className="text-[#92949d]">The intake your clients answer.</span></h1>
          <p className="mt-3 text-[14px] text-[#666971]">Every card here saves to your Champion Framework as you go. It shows the same sections afterwards, so nothing you set now is final.</p>
        </div>
        {ready && (
          <div className="partner-deck">
            <OrbitDeck
              cards={cards}
              onAnswer={() => undefined}
              onSkip={() => undefined}
              onDocument={() => undefined}
              onDone={() => finish('/partner/framework')}
              labels={{ deckTitle: 'Your deck', primary: 'Next', skip: '' }}
            />
          </div>
        )}
        {!ready && <p className="m-0 text-[14px] text-[#666971]">Dealing your cards.</p>}
      </main>
    </div>
  );
};

export default PartnerWelcome;
